import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ExternalLink, History } from 'lucide-react'
import { toast } from 'sonner'

import type { ShopContentDoc } from '@/lib/api/shop-content-tools-api'
import { ErrorText } from '@/components/retail/form-bits'
import { shopFieldErrors } from '@/components/retail/shop-errors'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { shopContentToolsApi } from '@/lib/api/shop-content-tools-api'
import { m } from '@/lib/paraglide/messages'

const fmt = (iso: string) => new Date(iso).toLocaleString('vi-VN')

/** Khoá cache của màn soạn tương ứng (để nạp lại sau khi khôi phục). */
const detailKey = (doc: ShopContentDoc, id: string) => [
    doc === 'posts' ? 'shop-post' : 'shop-page',
    id,
]

/**
 * Nút "Xem trước": mở tab ngay trong lúc bấm (trình duyệt không chặn popup)
 * rồi trỏ tới link ký sẵn khi server trả. Xem trước là bản ĐÃ LƯU gần nhất.
 */
export function PreviewButton({
    doc,
    id,
}: {
    doc: ShopContentDoc
    id: string
}) {
    const [error, setError] = useState<string | null>(null)
    const open = async () => {
        setError(null)
        const win = window.open('about:blank', '_blank')
        try {
            const res = await shopContentToolsApi.previewLink(doc, id)
            if (!res.data) throw new Error('empty')
            if (win) {
                win.opener = null
                win.location.href = res.data.url
            } else window.location.assign(res.data.url)
        } catch (err) {
            win?.close()
            setError(
                shopFieldErrors(err, m.cmsTools_preview_failed()).form ??
                    m.cmsTools_preview_failed(),
            )
        }
    }
    return (
        <div className="flex flex-col gap-1">
            <Button type="button" variant="outline" onClick={() => void open()}>
                <ExternalLink className="size-4" />
                {m.cmsTools_preview()}
            </Button>
            <p className="text-xs text-muted-foreground">
                {m.cmsTools_preview_hint()}
            </p>
            <ErrorText text={error} />
        </div>
    )
}

/** Lịch sử các lần Đăng: xem nội dung, khôi phục vào bản đang soạn. */
export function RevisionsPanel({
    doc,
    id,
    version,
    canWrite,
}: {
    doc: ShopContentDoc
    id: string
    version: number
    canWrite: boolean
}) {
    const qc = useQueryClient()
    const list = useQuery({
        queryKey: ['shop-revisions', doc, id, version],
        queryFn: () => shopContentToolsApi.revisions(doc, id),
    })
    const [viewing, setViewing] = useState<string | null>(null)
    const detail = useQuery({
        queryKey: ['shop-revision', doc, id, viewing],
        queryFn: () => shopContentToolsApi.revision(doc, id, viewing!),
        enabled: !!viewing,
    })
    const [error, setError] = useState<string | null>(null)
    const restore = useMutation({
        mutationFn: (rid: string) =>
            shopContentToolsApi.restore(doc, id, rid, version),
        onSuccess: async () => {
            toast.success(m.cmsTools_restored())
            await qc.invalidateQueries({ queryKey: detailKey(doc, id) })
        },
    })
    const doRestore = async (rid: string, title: string) => {
        if (!window.confirm(m.cmsTools_restore_confirm({ title }))) return
        setError(null)
        try {
            await restore.mutateAsync(rid)
        } catch (err) {
            // Lỗi theo ô (vd chuyên mục / ảnh cũ đã xoá) hiện ngay dưới danh sách.
            setError(
                Object.values(
                    shopFieldErrors(err, m.cmsTools_restore_failed()),
                ).join(' '),
            )
        }
    }
    const rows = list.data?.data ?? []
    const snap = detail.data?.data?.snapshot ?? {}
    return (
        <Card>
            <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                    <History className="size-4" />
                    {m.cmsTools_history()}
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
                {list.isError && (
                    <ErrorText text={m.cmsTools_history_load_failed()} />
                )}
                {!list.isLoading && rows.length === 0 && (
                    <p className="text-sm text-muted-foreground">
                        {m.cmsTools_history_empty()}
                    </p>
                )}
                <ul className="space-y-2">
                    {rows.map((r) => (
                        <li
                            key={r.id}
                            className="flex flex-wrap items-center justify-between gap-2 text-sm"
                        >
                            <span>
                                <b>{fmt(r.created_at)}</b>
                                {r.by_name ? ` · ${r.by_name}` : ''}
                                <span className="block text-muted-foreground">
                                    {r.title}
                                </span>
                            </span>
                            <span className="flex gap-1">
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => setViewing(r.id)}
                                >
                                    {m.cmsTools_view()}
                                </Button>
                                {canWrite && (
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant="outline"
                                        disabled={restore.isPending}
                                        onClick={() =>
                                            void doRestore(r.id, r.title)
                                        }
                                    >
                                        {m.cmsTools_restore()}
                                    </Button>
                                )}
                            </span>
                        </li>
                    ))}
                </ul>
                <ErrorText text={error} />
            </CardContent>
            <Dialog
                open={!!viewing}
                onOpenChange={(o) => !o && setViewing(null)}
            >
                <DialogContent className="flex max-h-[85vh] flex-col sm:max-w-3xl">
                    <DialogHeader>
                        <DialogTitle>{String(snap.title ?? '')}</DialogTitle>
                        <DialogDescription>
                            {m.cmsTools_view_hint()}
                        </DialogDescription>
                    </DialogHeader>
                    <pre className="min-h-0 flex-1 overflow-auto whitespace-pre-wrap rounded-md bg-muted p-3 text-xs">
                        {String(snap.content_html ?? '')}
                    </pre>
                </DialogContent>
            </Dialog>
        </Card>
    )
}
